import React from 'react';
import { Box, BoxProps, Flex, Text, Tooltip } from '@chakra-ui/react';
import { Check } from 'phosphor-react';

export interface IStepDef {
  key: number | string;
  label: string;
}

interface WizardStepperProps {
  steps: IStepDef[];
  currentStep: number | string;
  /**
   * Navegacao direta pelas bolinhas. Sem callback, o stepper e' so' leitura.
   * Quem valida o caminho ao pular para a frente e' o pai (o mesmo `validateStep`
   * do Continuar); aqui so' se decide o que e' clicavel.
   */
  // eslint-disable-next-line no-unused-vars
  onStepClick?: (stepKey: number | string) => void;
  /**
   * Indice da etapa mais adiantada ja' alcancada. Ate' ela, as bolinhas sao
   * clicaveis nos dois sentidos: quem voltou da Revisao para Socios volta direto
   * para a Revisao (28/09: antes so' as ANTERIORES a' atual eram clicaveis).
   * Sem o valor, vale a regra antiga (so' as anteriores).
   */
  alcancadoIdx?: number;
}

type EstadoDaEtapa = 'feita' | 'atual' | 'adiante' | 'fechada';

// Visual por estado (29/09, pedido do user: "estilizar mais as bolinhas"). A atual e'
// maior e tem halo; feita e adiante (ja' alcancada) tem o verde; fechada e' so' contorno.
const CIRCULO: Record<EstadoDaEtapa, BoxProps> = {
  feita: {
    w: '34px',
    h: '34px',
    bgGradient: 'linear(to-br, green.400, green.600)',
    color: 'white',
    boxShadow: '0 4px 10px -3px rgba(22, 163, 74, 0.55)',
  },
  atual: {
    w: '38px',
    h: '38px',
    bgGradient: 'linear(to-br, #45A6FF, #1467C4)',
    color: 'white',
    boxShadow: '0 0 0 5px rgba(27, 136, 245, 0.15), 0 6px 14px -4px rgba(27, 136, 245, 0.6)',
  },
  adiante: {
    w: '34px',
    h: '34px',
    bg: 'white',
    borderWidth: '2px',
    borderColor: 'green.400',
    color: 'green.600',
    boxShadow: '0 2px 6px -2px rgba(22, 163, 74, 0.35)',
  },
  fechada: {
    w: '34px',
    h: '34px',
    bg: 'white',
    borderWidth: '2px',
    borderColor: 'gray.200',
    color: 'gray.400',
  },
};

const ROTULO: Record<EstadoDaEtapa, { color: string; fontWeight: string }> = {
  feita: { color: 'gray.700', fontWeight: '600' },
  atual: { color: 'blue.600', fontWeight: '700' },
  adiante: { color: 'gray.600', fontWeight: '500' },
  fechada: { color: 'gray.400', fontWeight: '500' },
};

// Altura da faixa das bolinhas (a atual, maior): as linhas ficam no MEIO dela.
const ALTURA_DA_FAIXA = 38;
const ESPESSURA_DA_LINHA = 3;

export const WizardStepper = ({
  steps,
  currentStep,
  onStepClick,
  alcancadoIdx,
}: WizardStepperProps) => {
  // Em wizards dinamicos, `currentStep` e' uma key string: o indice vem da lista.
  const currentIdx = steps.findIndex(s => s.key === currentStep);
  const limite = Math.max(alcancadoIdx ?? currentIdx, currentIdx);
  return (
    <Flex
      gap={0}
      align="flex-start"
      justify="center"
      py={4}
      wrap="wrap"
      as="nav"
      aria-label="Etapas"
    >
      {steps.map((step, idx) => {
        let estado: EstadoDaEtapa = 'fechada';
        if (idx < currentIdx) estado = 'feita';
        else if (idx === currentIdx) estado = 'atual';
        // Ja' alcancada, adiante da atual: volta-se a ela sem refazer o caminho.
        else if (idx <= limite) estado = 'adiante';
        const isClickable = !!onStepClick && (estado === 'feita' || estado === 'adiante');
        let corDaLinha = 'gray.200';
        if (estado === 'feita') corDaLinha = 'green.400';
        else if (idx < limite) corDaLinha = 'green.200';
        let dica = '';
        if (estado === 'feita') dica = `Voltar para "${step.label}"`;
        else if (estado === 'adiante') dica = `Ir para "${step.label}"`;
        else if (estado === 'fechada') dica = 'Avance pelo Continuar para chegar aqui';
        const handleClick = isClickable ? () => onStepClick!(step.key) : undefined;
        const estiloDoCirculo: BoxProps = {
          ...CIRCULO[estado],
          borderRadius: 'full',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: 'sm',
          transition: 'transform 0.15s, box-shadow 0.15s',
        };
        const conteudo =
          estado === 'feita' || estado === 'adiante' ? <Check size={17} weight="bold" /> : idx + 1;
        // Clicável vira <button> de verdade: alcança pelo Tab e abre com Enter/Espaço.
        const circle = isClickable ? (
          <Box
            as="button"
            type="button"
            aria-label={dica}
            {...estiloDoCirculo}
            cursor="pointer"
            onClick={handleClick}
            _hover={{ transform: 'translateY(-2px)' }}
            _focusVisible={{ outline: '2px solid', outlineColor: 'blue.400', outlineOffset: '2px' }}
          >
            {conteudo}
          </Box>
        ) : (
          <Box
            aria-current={estado === 'atual' ? 'step' : undefined}
            {...estiloDoCirculo}
            cursor="default"
          >
            {conteudo}
          </Box>
        );
        return (
          <Flex key={step.key} align="flex-start">
            {/* 7 etapas (alteração com Sócios) cabem numa linha em 1440: com 120 px +
                60 px de linha, a Revisão caía para a linha de baixo. */}
            <Flex direction="column" align="center" minW={{ base: '64px', md: '96px' }}>
              <Flex h={`${ALTURA_DA_FAIXA}px`} align="center" justify="center">
                {dica ? (
                  <Tooltip label={dica} placement="top" hasArrow openDelay={300}>
                    {circle}
                  </Tooltip>
                ) : (
                  circle
                )}
              </Flex>
              <Text
                mt={2}
                fontSize="13px"
                textAlign="center"
                lineHeight="1.25"
                {...ROTULO[estado]}
                cursor={isClickable ? 'pointer' : 'default'}
                onClick={handleClick}
                _hover={isClickable ? { color: 'gray.900' } : undefined}
              >
                {step.label}
              </Text>
            </Flex>
            {idx < steps.length - 1 && (
              <Box
                w={{ base: '12px', md: '36px' }}
                h={`${ESPESSURA_DA_LINHA}px`}
                mt={`${(ALTURA_DA_FAIXA - ESPESSURA_DA_LINHA) / 2}px`}
                borderRadius="full"
                bg={corDaLinha}
                flexShrink={0}
              />
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};

export default WizardStepper;
